import React, { useEffect, useState, useCallback } from 'react';
import { SectionTitle } from './PageShell';
import AdminShell from './AdminShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import {
  Sparkles, Loader2, Plus, Trash2, Eye, EyeOff, Newspaper,
  Quote, Lightbulb, Target, Car, Bike, Trophy, ShieldAlert, MessageSquare, Check,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const TYPE_LABEL = {
  citation: { label: 'Citation', Icon: Quote, color: 'text-amber-400' },
  saviez_vous: { label: 'Le saviez-vous', Icon: Lightbulb, color: 'text-sky-400' },
  defi: { label: 'Défi', Icon: Target, color: 'text-emerald-400' },
  actu_auto: { label: 'Actu auto', Icon: Car, color: 'text-slate-300' },
  actu_moto: { label: 'Actu moto', Icon: Bike, color: 'text-red-400' },
  sport_meca: { label: 'Sport méca', Icon: Trophy, color: 'text-violet-400' },
  regle_officielle: { label: 'Règle officielle', Icon: ShieldAlert, color: 'text-red-500' },
  post_enseignant: { label: 'Post enseignant', Icon: MessageSquare, color: 'text-blue-400' },
};

export default function AdminActuPage() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ type: 'citation', title: '', body: '', accent: 'red', source_label: '', source_url: '' });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.adminActuList();
      setItems(d.items || []);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { if (!authLoading && user?.is_admin) refresh(); }, [authLoading, user, refresh]);

  const generate = async () => {
    setGenerating(true); setErr(''); setMsg('');
    try {
      const r = await api.adminActuGenerate();
      setMsg(`${r.count} item(s) généré(s) par l'IA.`);
      await refresh();
      setTimeout(() => setMsg(''), 4000);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Erreur de génération');
    }
    setGenerating(false);
  };

  const toggleActive = async (it) => {
    try { await api.adminActuUpdate(it.id, { active: !it.active }); await refresh(); } catch (_) { /* silent */ }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cet item ?')) return;
    try { await api.adminActuDelete(id); await refresh(); } catch (_) { /* silent */ }
  };

  const createManual = async (e) => {
    e.preventDefault();
    try {
      await api.adminActuCreate(form);
      setShowNew(false);
      setForm({ type: 'citation', title: '', body: '', accent: 'red', source_label: '', source_url: '' });
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Erreur');
    }
  };

  if (authLoading) return <AdminShell><div className="max-w-6xl mx-auto p-12 text-white/50">Chargement…</div></AdminShell>;
  if (!user?.is_admin) return <AdminShell><div className="max-w-3xl mx-auto p-12 text-center text-white/60">Accès réservé aux administrateurs.</div></AdminShell>;

  return (
    <AdminShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-8">
        <SectionTitle kicker="Admin · Actu & Fun" title="Gestion du carrousel du jour" sub="Bibliothèque d'items (citations, faits, défis, actu auto/moto). Génère du contenu IA quotidien." />

        <div className="flex items-center gap-3 flex-wrap mb-6">
          <button
            onClick={generate}
            disabled={generating}
            data-testid="admin-actu-generate"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-violet-500 to-red-500 hover:opacity-90 text-white font-semibold text-sm disabled:opacity-50"
          >
            {generating ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
            Générer 3 items du jour (IA · Claude)
          </button>
          <button
            onClick={() => setShowNew(true)}
            data-testid="admin-actu-new"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 text-white font-semibold text-sm border border-white/10"
          >
            <Plus size={16} /> Nouveau manuel
          </button>
          <Link to="/admin" className="ml-auto text-[13px] text-white/60 hover:text-white">← Dashboard admin</Link>
        </div>

        {msg && <div className="mb-4 text-emerald-400 text-sm inline-flex items-center gap-1"><Check size={14} /> {msg}</div>}
        {err && <div className="mb-4 text-red-400 text-sm">{err}</div>}

        {loading ? (
          <div className="text-white/40">Chargement…</div>
        ) : (
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="grid grid-cols-[auto_1fr_auto_auto] gap-3 px-4 py-3 bg-white/5 text-[11px] uppercase tracking-widest text-white/60 font-bold">
              <div>Type</div><div>Contenu</div><div>Statut</div><div>Actions</div>
            </div>
            {items.length === 0 && <div className="p-8 text-center text-white/40">Aucun item. Génère du contenu ci-dessus.</div>}
            {items.map((it) => {
              const meta = TYPE_LABEL[it.type] || TYPE_LABEL.citation;
              const Icon = meta.Icon;
              return (
                <div key={it.id} className="grid grid-cols-[auto_1fr_auto_auto] gap-3 px-4 py-3 border-t border-white/5 items-center" data-testid={`admin-actu-row-${it.id}`}>
                  <div className={`inline-flex items-center gap-1.5 text-[11px] font-bold ${meta.color}`}>
                    <Icon size={13} /> {meta.label}
                  </div>
                  <div className="min-w-0">
                    <div className="text-white text-sm truncate">
                      {it.title}
                      {it.ai_generated && <span className="ml-2 text-[9px] uppercase text-violet-400">IA</span>}
                      {it.author_type === 'teacher' && <span className="ml-2 text-[9px] uppercase text-blue-400">Enseignant</span>}
                    </div>
                    <div className="text-white/50 text-[12px] truncate">{it.body}</div>
                  </div>
                  <button
                    onClick={() => toggleActive(it)}
                    data-testid={`admin-actu-toggle-${it.id}`}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 ${it.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-white/40'}`}
                  >
                    {it.active ? <Eye size={11} /> : <EyeOff size={11} />}
                    {it.active ? 'Actif' : 'Masqué'}
                  </button>
                  <button
                    onClick={() => remove(it.id)}
                    data-testid={`admin-actu-delete-${it.id}`}
                    className="p-2 text-white/50 hover:text-red-400"
                    title="Supprimer"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {showNew && (
          <div className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
            <form onSubmit={createManual} className="w-full max-w-lg rounded-2xl bg-[#111] border border-white/15 p-6" onClick={(e) => e.stopPropagation()}>
              <h4 className="font-serif-editorial text-white text-[22px] mb-4"><Newspaper className="inline mr-2" size={20} /> Nouvel item</h4>
              <label className="block text-[11px] uppercase text-white/60 mb-1">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white mb-3">
                {Object.entries(TYPE_LABEL).filter(([k]) => k !== 'post_enseignant').map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <input type="text" required placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full mb-3 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white" />
              <textarea required placeholder="Corps du message" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full mb-3 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white resize-none" />
              <input type="text" placeholder="Source label (optionnel)" value={form.source_label} onChange={(e) => setForm({ ...form, source_label: e.target.value })} className="w-full mb-3 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white" />
              <input type="url" placeholder="Source URL (optionnel)" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} className="w-full mb-3 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white" />
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 rounded-full text-white/60 hover:text-white text-sm">Annuler</button>
                <button type="submit" className="px-5 py-2 rounded-full bg-red-500 hover:bg-red-400 text-white font-semibold text-sm">Créer</button>
              </div>
            </form>
          </div>
        )}
      </section>
    </AdminShell>
  );
}
